// SyncPlayer: owns the <video> element and the hls.js lifecycle.
//
// Responsibilities:
//   - Apply authoritative state from the server (src, play, pause, seek)
//   - Send syncReport every 5s and apply server's syncDirective
//   - Prevent feedback loops via an `applyingRemoteState` guard
//   - Custom controls that emit ONLY play/pause/seek commands
//   - Surface buffering, error, and autoplay-blocked states
//
// All media-event-to-command mapping is done here. Chat/presence/name events
// from the parent do not flow into this component as playback commands.

import { useEffect, useRef, useState, useCallback } from "react";
import Hls from "hls.js";
import type HlsType from "hls.js";
import type { PlaybackState, SyncDirectiveMsg } from "@shared/protocol";
import { PlayerOverlay, type PlayerOverlayProps } from "./PlayerOverlay.js";
import {
  PLAYING_DRIFT_THRESHOLD_SEC,
  PAUSED_DRIFT_THRESHOLD_SEC,
  SYNC_REPORT_INTERVAL_MS
} from "@shared/playback";

export interface SyncPlayerProps {
  playback: PlaybackState | null;
  appliedRevision: number;
  syncDirective: SyncDirectiveMsg | null;
  onPlay: () => void;
  onPause: () => void;
  onSeek: (currentTime: number) => void;
  sendSyncReport: (data: {
    currentTime: number;
    isPlaying: boolean;
    readyState: number;
    buffering: boolean;
    sourceRevision: number;
  }) => void;
}

export function SyncPlayer(props: SyncPlayerProps): JSX.Element {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const hlsRef = useRef<HlsType | null>(null);
  const [autoplayBlocked, setAutoplayBlocked] = useState(false);
  const [hlsError, setHlsError] = useState<string | null>(null);
  const [buffering, setBuffering] = useState(false);
  const [localTime, setLocalTime] = useState(0);
  const [localPaused, setLocalPaused] = useState(true);

  // The most recently applied authoritative src and revision.
  const lastAppliedSrcRef = useRef<string | null>(null);
  const lastAppliedRevRef = useRef<number>(-1);
  // Pending authoritative action (seek + play/pause) to apply once MANIFEST_PARSED.
  const pendingRef = useRef<PlaybackState | null>(null);
  // Guard so local media events don't echo commands back to the server.
  const applyingRemoteRef = useRef(false);

  // ----- Build / destroy HLS when src changes -----
  useEffect(() => {
    const video = videoRef.current;
    const p = props.playback;
    if (!video) return;
    if (!p || !p.src) {
      hlsRef.current?.destroy();
      hlsRef.current = null;
      lastAppliedSrcRef.current = null;
      return;
    }

    if (lastAppliedSrcRef.current === p.src && hlsRef.current) return;
    // New src — destroy old, build new.
    hlsRef.current?.destroy();
    hlsRef.current = null;
    setHlsError(null);
    setAutoplayBlocked(false);
    pendingRef.current = p;
    lastAppliedSrcRef.current = p.src;

    if (Hls.isSupported()) {
      const hls = new Hls({
        enableWorker: true,
        lowLatencyMode: false
      });
      hls.attachMedia(video);
      hls.on(Hls.Events.MEDIA_ATTACHED, () => {
        hls.loadSource(p.src!);
      });
      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        applyPendingState(video, hls, pendingRef.current, applyingRemoteRef, setAutoplayBlocked);
      });
      hls.on(Hls.Events.ERROR, (_evt, data) => {
        if (!data.fatal) return;
        if (data.type === Hls.ErrorTypes.MEDIA_ERROR) {
          hls.recoverMediaError();
          return;
        }
        setHlsError(`${data.type}: ${data.details}`);
      });
      hlsRef.current = hls;
    } else if (video.canPlayType("application/vnd.apple.mpegurl")) {
      // Safari native HLS.
      applyingRemoteRef.current = true;
      video.src = p.src;
      video.addEventListener(
        "loadedmetadata",
        () => applyPendingState(video, null, pendingRef.current, applyingRemoteRef, setAutoplayBlocked),
        { once: true }
      );
    } else {
      setHlsError("HLS not supported in this browser");
    }

    return () => {
      // On dep change the next run will replace; nothing to do here.
    };
  }, [props.playback?.src]);

  // ----- Apply subsequent authoritative state changes -----
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !props.playback) return;
    if (props.appliedRevision === lastAppliedRevRef.current) return;
    lastAppliedRevRef.current = props.appliedRevision;

    if (lastAppliedSrcRef.current !== props.playback.src) {
      // src-change handled by the dedicated effect above.
      return;
    }
    pendingRef.current = props.playback;
    // If HLS is ready, apply now. Otherwise MANIFEST_PARSED handler will.
    const hls = hlsRef.current;
    const canApply = hls
      ? hls.levels && hls.levels.length > 0
      : video.readyState >= 1;
    if (canApply) {
      applyPendingState(video, hls, props.playback, applyingRemoteRef, setAutoplayBlocked);
    }
  }, [props.appliedRevision, props.playback]);

  // ----- Apply syncDirective -----
  useEffect(() => {
    const video = videoRef.current;
    const dir = props.syncDirective;
    if (!video || !dir) return;
    if (dir.sourceRevision !== props.playback?.sourceRevision) return;
    if (dir.seekTo !== null) {
      const t = clampSeekable(video, dir.seekTo);
      applyingRemoteRef.current = true;
      video.currentTime = t;
      setLocalTime(t);
    }
    if (dir.shouldPlay && video.paused) {
      applyingRemoteRef.current = true;
      void video.play().catch(() => setAutoplayBlocked(true));
    } else if (!dir.shouldPlay && !video.paused) {
      applyingRemoteRef.current = true;
      video.pause();
    }
  }, [props.syncDirective, props.playback?.sourceRevision]);

  // ----- Periodic sync reports -----
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    const interval = setInterval(() => {
      const p = props.playback;
      if (!p || !p.src) return;
      props.sendSyncReport({
        currentTime: video.currentTime,
        isPlaying: !video.paused,
        readyState: video.readyState,
        buffering: buffering,
        sourceRevision: p.sourceRevision
      });
    }, SYNC_REPORT_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [props.playback?.src, buffering, props.sendSyncReport, props.playback]);

  // ----- Local media events -> UI state only (no commands) -----
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    const onTime = () => setLocalTime(video.currentTime);
    const onPause = () => {
      setLocalPaused(true);
      if (!applyingRemoteRef.current) {
        // Local pause: emit to server.
        props.onPause();
      }
      applyingRemoteRef.current = false;
    };
    const onPlay = () => {
      setLocalPaused(false);
      if (!applyingRemoteRef.current) {
        props.onPlay();
      }
      applyingRemoteRef.current = false;
    };
    const onWaiting = () => setBuffering(true);
    const onPlaying = () => setBuffering(false);
    video.addEventListener("timeupdate", onTime);
    video.addEventListener("pause", onPause);
    video.addEventListener("play", onPlay);
    video.addEventListener("waiting", onWaiting);
    video.addEventListener("playing", onPlaying);
    return () => {
      video.removeEventListener("timeupdate", onTime);
      video.removeEventListener("pause", onPause);
      video.removeEventListener("play", onPlay);
      video.removeEventListener("waiting", onWaiting);
      video.removeEventListener("playing", onPlaying);
    };
  }, [props.onPause, props.onPlay]);

  // ----- Custom controls -----
  const togglePlay = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    if (video.paused) {
      void video.play().catch(() => setAutoplayBlocked(true));
    } else {
      video.pause();
    }
  }, []);

  const seekBy = useCallback(
    (deltaSec: number) => {
      const video = videoRef.current;
      if (!video) return;
      const target = clampSeekable(video, video.currentTime + deltaSec);
      video.currentTime = target;
      setLocalTime(target);
      props.onSeek(target);
    },
    [props.onSeek]
  );

  const handleScrub = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const video = videoRef.current;
      if (!video) return;
      const v = Number(e.target.value);
      video.currentTime = v;
      setLocalTime(v);
    },
    []
  );

  const handleScrubCommit = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    props.onSeek(video.currentTime);
  }, [props.onSeek]);

  const unlockAutoplay = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    setAutoplayBlocked(false);
    // Resume local playback without sending a global play command; the server
    // already has the authoritative state. If the server says playing, we
    // just start local decoding.
    void video.play().catch(() => setAutoplayBlocked(true));
  }, []);

  const playback = props.playback;
  const duration = playback?.duration ?? videoRef.current?.duration ?? 0;
  const isPlaying = playback?.isPlaying ?? false;

  const overlay = selectOverlay({
    playback,
    hlsError,
    autoplayBlocked,
    buffering,
    onUnlockAutoplay: unlockAutoplay
  });

  return (
    <div className="player">
      <div className="player-frame">
        <video ref={videoRef} playsInline controls={false} />
        {overlay && <PlayerOverlay {...overlay} />}
      </div>
      <div className="controls">
        <button onClick={togglePlay} aria-label={localPaused ? "Play" : "Pause"}>
          {localPaused ? "▶" : "❚❚"}
        </button>
        <button onClick={() => seekBy(-10)} aria-label="Back 10s">−10s</button>
        <button onClick={() => seekBy(10)} aria-label="Forward 10s">+10s</button>
        <input
          type="range"
          min={0}
          max={Number.isFinite(duration) && duration > 0 ? duration : 0}
          step={0.1}
          value={localTime}
          onChange={handleScrub}
          onMouseUp={handleScrubCommit}
          onTouchEnd={handleScrubCommit}
          onKeyUp={handleScrubCommit}
          aria-label="Seek"
        />
        <span className="time">
          {fmt(localTime)} / {fmt(duration)}
        </span>
        <span className="state">
          {isPlaying ? "Playing" : "Paused"}
        </span>
      </div>
    </div>
  );
}

/**
 * Decide which single overlay to show, most urgent first.
 *
 * Order matters: a paused stream also reports `waiting` on some browsers
 * (the `playing` event never arrives while paused), which is exactly why
 * "Buffering…" used to stick on the devices that did NOT initiate the pause.
 * Checking paused before buffering keeps the honest message on top.
 */
function selectOverlay(args: {
  playback: PlaybackState | null;
  hlsError: string | null;
  autoplayBlocked: boolean;
  buffering: boolean;
  onUnlockAutoplay: () => void;
}): PlayerOverlayProps | null {
  const { playback, hlsError, autoplayBlocked, buffering } = args;

  if (hlsError) {
    return {
      variant: "error",
      title: "Playback error",
      detail: hlsError
    };
  }
  if (!playback || !playback.src) {
    return {
      variant: "empty",
      title: "Load a source to begin watching."
    };
  }
  if (autoplayBlocked) {
    return {
      variant: "autoplay",
      title: "Playback is blocked by your browser",
      detail: "Tap to start watching in sync.",
      action: <button onClick={args.onUnlockAutoplay}>Enable playback</button>
    };
  }
  if (!playback.isPlaying) {
    const who = playback.pausedBy?.name;
    return {
      variant: "paused",
      icon: "❚❚",
      title: who ? `${who} paused the stream` : "Paused",
      detail: who ? undefined : "Press play when you're ready."
    };
  }
  if (buffering) {
    return {
      variant: "buffering",
      title: "Buffering…"
    };
  }
  return null;
}

function applyPendingState(
  video: HTMLVideoElement,
  _hls: HlsType | null,
  state: PlaybackState | null,
  guardRef: React.MutableRefObject<boolean>,
  setAutoplayBlocked: (v: boolean) => void
): void {
  if (!state) return;
  guardRef.current = true;
  const target = clampSeekable(video, state.currentTime);
  if (Math.abs(video.currentTime - target) > 0.05) {
    video.currentTime = target;
  }
  if (state.isPlaying && video.paused) {
    void video.play().catch(() => {
      guardRef.current = false;
      setAutoplayBlocked(true);
    });
  } else if (!state.isPlaying && !video.paused) {
    video.pause();
  }
  // Release guard on next tick (after native events flush).
  setTimeout(() => {
    guardRef.current = false;
  }, 0);
}

function clampSeekable(video: HTMLVideoElement, t: number): number {
  if (!Number.isFinite(t)) return 0;
  if (t < 0) return 0;
  try {
    const seekable = video.seekable;
    if (seekable && seekable.length > 0) {
      const start = seekable.start(0);
      const end = seekable.end(seekable.length - 1);
      if (t < start) return start;
      if (t > end) return end;
    }
  } catch {
    // ignore
  }
  return t;
}

function fmt(s: number): string {
  if (!Number.isFinite(s)) return "0:00";
  const total = Math.floor(s);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const sec = total % 60;
  const mm = m.toString().padStart(h > 0 ? 2 : 1, "0");
  const ss = sec.toString().padStart(2, "0");
  return h > 0 ? `${h}:${mm}:${ss}` : `${mm}:${ss}`;
}

// Suppress lint about unused params (hls reserved for future use).
void PLAYING_DRIFT_THRESHOLD_SEC;
void PAUSED_DRIFT_THRESHOLD_SEC;